import { useAtom } from 'jotai';
import { useEffect, useMemo } from 'react';

import { useCart } from '$hooks/queries';
import { usePickupPoints } from '$hooks/queries/usePickupPoints';
import { lastUserPickupPointAtom } from '$src/business/atoms/last-pickup-point';

/**
 * Warms the pickup-points cache as soon as the cart has both a complete
 * shipping address and at least one pickup-capable carrier in its methods,
 * regardless of which segment the user is currently on.
 *
 * The first time the user taps the "Relais" segment, `useAutoSelectPickupPoint`
 * calls `usePickupPoints(selectedCarrier.pickup, search)` with the same
 * provider+search key — React-Query dedupes the query by key, so the result
 * comes straight from cache (typically <1ms instead of 1–3s network round
 * trip via `/api/${shopId}/search-pickup-points`). The "Chargement des points
 * de retrait…" flash that used to bridge the gap disappears.
 *
 * The query is internally gated by `usePickupPoints`'s `enabled` flag
 * (provider + 4 address fields all required), so the hook is a no-op until
 * the address is complete or before the cart's shipping methods have
 * loaded — no wasted requests during the typical "user is filling the
 * email/coordinates" phase.
 *
 * Only the FIRST pickup-capable carrier is prefetched. Shops with multiple
 * pickup providers (rare) accept a cold-cache fetch on the non-first one;
 * fanning out to all providers would multiply mobile network cost without
 * touching the common case.
 *
 * Mount once at the top of the Delivery / ShippingMethods tree. No params,
 * no return value — autonomous like `useAutoSelectPickupPoint`.
 */
export const usePrefetchPickupPoints = () => {
  const { data: cart } = useCart();
  const [lastUserPickupPoint, setLastUserPickupPoint] = useAtom(lastUserPickupPointAtom);

  const pickupProvider = useMemo(() => {
    const pickupMethod = (cart?.shipping?.methods ?? []).find(
      (method) => method.carriers?.[0]?.pickup,
    );
    return pickupMethod?.carriers?.[0]?.pickup;
  }, [cart?.shipping?.methods]);

  const search = useMemo(
    () => ({
      address1: cart?.shipping?.address?.address1 ?? '',
      country: cart?.shipping?.address?.countryCode ?? '',
      zip: cart?.shipping?.address?.zip ?? '',
      city: cart?.shipping?.address?.city ?? '',
    }),
    [cart?.shipping?.address],
  );

  // Returns are mostly ignored — `useAutoSelectPickupPoint` re-reads from
  // cache by querying the same key when the user actually enters Pickup
  // mode. We read `data` only to seed the optimistic atom below.
  const { data } = usePickupPoints(pickupProvider, search);

  // Seed `lastUserPickupPointAtom` with the closest point as soon as the
  // prefetched list lands, IF the atom is still empty (no prior user pick
  // this session). This unlocks the optimistic-display path in
  // MobileCategoryDetail before the user has ever entered Pickup mode —
  // first-visit-to-Relais feels as instant as subsequent re-entries.
  // Semantically the atom shifts from "user's last pick" to "next-most-
  // likely pickup point"; the autoselect hook already treats it as a
  // preference hint (`data.find(id === atom.id) ?? data[0]`) so the seeded
  // value is a no-op once autoselect runs with the same data set.
  useEffect(() => {
    if (lastUserPickupPoint) return;
    const first = data?.[0];
    if (!first) return;
    setLastUserPickupPoint(first);
  }, [data, lastUserPickupPoint, setLastUserPickupPoint]);
};
